"use client";
import React from "react";
import {
  Box,
  Separator,
  Table,
  Text,
  VStack,
  Stack,
  HStack,
  SimpleGrid,
  Center,
} from "@chakra-ui/react";
import { Heading } from "@/elements";
import { useHeadingVariant } from "@/theme/hooks/useSectionStyle";
import { SECURITY_COMPLIANCE_BUNDLES } from "./constants";
import { FaShieldAlt } from "@onemind-services-llc/react-icons-ng/fa";

export const DesktopView = () => {
  return (
    <Box overflowX="auto" w="100%">
      <Table.Root striped border="2px solid rgb(237,242,247)" width="100%">
        <Table.Header>
          <Table.Row tableLayout="fixed">
            <Table.ColumnHeader
              whiteSpace="pre-line"
              textTransform="none"
              fontSize="0.9rem"
            >
              Minimum User Tier
            </Table.ColumnHeader>
            <Table.ColumnHeader
              whiteSpace="pre-line"
              textTransform="none"
              fontSize="0.9rem"
              px={1}
            >
              Per User Monthly Price
            </Table.ColumnHeader>
            <Table.ColumnHeader
              whiteSpace="pre-line"
              textTransform="none"
              fontSize="0.9rem"
            >
              Microsoft License
            </Table.ColumnHeader>
            <Table.ColumnHeader
              whiteSpace="pre-line"
              textTransform="none"
              fontSize="0.9rem"
            >
              Security Stack
            </Table.ColumnHeader>
            <Table.ColumnHeader
              whiteSpace="pre-line"
              textTransform="none"
              fontSize="0.9rem"
            >
              Support Hours/Month
            </Table.ColumnHeader>
            <Table.ColumnHeader
              whiteSpace="pre-line"
              textTransform="none"
              fontSize="0.9rem"
            >
              vCISCO + Audit Defence (Optional)
            </Table.ColumnHeader>
            <Table.ColumnHeader
              whiteSpace="pre-line"
              textTransform="none"
              fontSize="0.9rem"
            >
              Governance Deliverables
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {SECURITY_COMPLIANCE_BUNDLES.map((bundle, index) => (
            <Table.Row key={index}>
              <Table.Cell whiteSpace="pre-line" fontSize="15px">
                {bundle.minUserTier}
              </Table.Cell>
              <Table.Cell whiteSpace="pre-line" fontSize="15px">
                <Text fontWeight="700" color="primary.200" fontSize="18px">
                  {bundle.pricePerUser}
                </Text>
              </Table.Cell>
              <Table.Cell whiteSpace="pre-line" fontSize="15px">
                {bundle.license}
              </Table.Cell>
              <Table.Cell whiteSpace="pre-line" fontSize="15px">
                {bundle.firewall}
              </Table.Cell>
              <Table.Cell whiteSpace="pre-line" fontSize="15px">
                {bundle.supportHours}
              </Table.Cell>
              <Table.Cell whiteSpace="pre-line" fontSize="15px">
                {bundle.vCISO}
              </Table.Cell>
              <Table.Cell whiteSpace="pre-line" fontSize="15px">
                {bundle.governance}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export const MobileView = () => {
  return (
    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={6}>
      {SECURITY_COMPLIANCE_BUNDLES.map((bundle, index) => (
        <VStack
          key={index}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="md"
          gap={4}
          w="100%"
          p={4}
          bg="white"
        >
          <HStack gap={3} align="center" w="full">
            <Box
              h="2.5rem"
              w="2.5rem"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="md"
              bg="gray.50"
            >
              <FaShieldAlt color="#3182CE" size="1.5rem" />
            </Box>
            <Text fontWeight="bold" fontSize="lg">
              {bundle.license}
            </Text>
          </HStack>

          <Separator borderColor="gray.200" />

          <Stack gap={2} w="full">
            <Text fontSize="sm" color="gray.600">
              Minimum User Tier
            </Text>
            <Text fontWeight="medium">{bundle.minUserTier}</Text>
          </Stack>

          <Stack gap={2} w="full">
            <Text fontSize="sm" color="gray.600">
              Per User Monthly Price
            </Text>
            <Text fontWeight="bold" fontSize="lg" color="primary.200">
              {bundle.pricePerUser}
            </Text>
          </Stack>

          <Stack gap={2} w="full">
            <Text fontSize="sm" color="gray.600">
              Firewall (Included)
            </Text>
            <Text fontWeight="medium">{bundle.firewall}</Text>
          </Stack>

          <Stack gap={2} w="full">
            <Text fontSize="sm" color="gray.600">
              Support Hours/Month
            </Text>
            <Text fontWeight="medium">{bundle.supportHours}</Text>
          </Stack>

          <Stack gap={2} w="full">
            <Text fontSize="sm" color="gray.600">
              vCISO + Audit Defence (Optional)
            </Text>
            <Text fontWeight="medium">{bundle.vCISO}</Text>
          </Stack>

          <Stack gap={2} w="full">
            <Text fontSize="sm" color="gray.600">
              Governance Deliverables
            </Text>
            <Text fontSize="sm" whiteSpace="pre-line">
              {bundle.governance}
            </Text>
          </Stack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

const SecurityComplianceTable = ({ showHeading = true }: { showHeading?: boolean }) => {
  const HeadingVariant = useHeadingVariant();

  return (
    <Box id="microsoft-sec-bundles">
      {showHeading && (
        <Heading variant={HeadingVariant} textAlign="center" mb={8}>
          Fully Managed Security & Compliance Bundles
        </Heading>
      )}

      <Center display={{ md: "none" }} w="full">
        <MobileView />
      </Center>

      <Box display={{ base: "none", md: "block" }}>
        <DesktopView />
      </Box>
    </Box>
  );
};

export default SecurityComplianceTable;
